import {
  Accordion,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';

import { useDeleteDeviceVisits, useUpdateDeviceLink } from '../api/updateLink';
import { useUpdateDeviceLinkProfile } from '../api/updateLinkProfile';
import { FIXED_EXPECTED_DEVICE_MODEL, settingsPanelStyles } from '../constants';
import { LinkReport } from '../types';
import { fmtDate, osLabel } from '../utils/formatters';

import { EmptyNote, LinkUrlRow, ModelCell } from './DeviceTrackerHeader';

const inputLabelStyles = {
  label: { color: 'var(--mantine-color-text)' },
  description: { color: 'var(--mantine-color-dimmed)' },
};

type Props = {
  item: LinkReport;
};

export const LinkReportCard = ({ item }: Props) => {
  const deleteVisits = useDeleteDeviceVisits({
    config: {
      onSuccess: (data) => {
        notifications.show({
          color: 'teal',
          title: 'История очищена',
          message: `Удалено переходов: ${data.deleted}`,
        });
      },
      onError: () => {
        notifications.show({ color: 'red', title: 'Не удалось очистить историю', message: '' });
      },
    },
  });

  const updateProfile = useUpdateDeviceLinkProfile({
    config: {
      onSuccess: () => {
        notifications.show({ color: 'teal', title: 'Профиль сохранён', message: '' });
      },
      onError: () => {
        notifications.show({ color: 'red', title: 'Не удалось сохранить профиль', message: '' });
      },
    },
  });

  const [label, setLabel] = useState(item.label);
  const [slug, setSlug] = useState(item.slug);
  const [note, setNote] = useState(item.note || '');

  const updateLink = useUpdateDeviceLink({
    config: {
      onSuccess: () => {
        notifications.show({ color: 'teal', title: 'Ссылка обновлена', message: '' });
      },
      onError: (error) => {
        const message =
          (error.response?.data as { error?: string })?.error || 'Не удалось обновить ссылку';
        notifications.show({ color: 'red', title: 'Ошибка', message });
      },
    },
  });

  const handleSaveLink = () => {
    updateLink.mutate({
      slug: item.slug,
      label: label.trim(),
      newSlug: slug.trim() !== item.slug ? slug.trim() : undefined,
      note: note.trim(),
    });
  };

  const [profileScreen, setProfileScreen] = useState(item.expectedProfile?.screenKey || '');
  const [profileIos, setProfileIos] = useState(item.expectedProfile?.iosPrefix || '');

  const handleSaveProfile = () => {
    updateProfile.mutate({
      slug: item.slug,
      expectedProfile: {
        model: FIXED_EXPECTED_DEVICE_MODEL,
        screenKey: profileScreen.trim() || null,
        iosPrefix: profileIos.trim() || null,
      },
    });
  };

  const handleClearHistory = () => {
    if (!window.confirm(`Очистить всю историю для «${item.label}»?`)) return;
    deleteVisits.mutate(item.slug);
  };

  return (
    <Paper withBorder p="md" radius="md" id={`link-${item.slug}`}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={4}>
            <Text fw={600} size="lg">
              {item.label}
            </Text>
            <LinkUrlRow url={item.url} />
          </Stack>

          <Group gap="xs">
            <Badge variant="light">{item.totalVisits} переходов</Badge>
            <Badge variant="light">{item.uniqueDevices} устройств</Badge>
            <Badge variant="light">{item.uniqueIps} IP</Badge>
            {item.multipleDevices && (
              <Badge color="orange" variant="light">
                Разные устройства
              </Badge>
            )}
            {item.totalVisits > 0 && (
              <Button
                variant="subtle"
                color="red"
                size="compact-sm"
                loading={deleteVisits.isLoading}
                onClick={handleClearHistory}
              >
                Очистить историю
              </Button>
            )}
          </Group>
        </Group>

        <Paper withBorder p="sm" radius="sm" style={settingsPanelStyles.section}>
          <Text size="sm" fw={600} mb="xs" style={settingsPanelStyles.title}>
            Настройки ссылки
          </Text>
          <Group align="flex-end" wrap="wrap">
            <TextInput
              label="Название"
              placeholder="Иван — iPhone 17"
              value={label}
              onChange={(event) => setLabel(event.currentTarget.value)}
              styles={inputLabelStyles}
              style={{ flex: 1, minWidth: 180 }}
            />
            <TextInput
              label="Slug"
              placeholder="ivan-iphone-17"
              value={slug}
              onChange={(event) => setSlug(event.currentTarget.value)}
              styles={inputLabelStyles}
              style={{ flex: 1, minWidth: 140 }}
            />
            <TextInput
              label="Заметка"
              placeholder="необязательно"
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
              styles={inputLabelStyles}
              style={{ flex: 1, minWidth: 160 }}
            />
            <Button size="sm" loading={updateLink.isLoading} onClick={handleSaveLink}>
              Сохранить
            </Button>
          </Group>
        </Paper>

        <Paper withBorder p="sm" radius="sm" style={settingsPanelStyles.section}>
          <Text size="sm" fw={600} mb="xs" style={settingsPanelStyles.title}>
            Ожидаемый профиль (для MATCH)
          </Text>
          <Group align="flex-end" wrap="wrap">
            <TextInput
              label="Модель"
              value={FIXED_EXPECTED_DEVICE_MODEL}
              readOnly
              description="Фиксированное значение"
              styles={{
                ...inputLabelStyles,
                input: {
                  cursor: 'default',
                  color: 'var(--mantine-color-text)',
                  backgroundColor: 'var(--mantine-color-default-hover)',
                },
              }}
              style={{ flex: 1, minWidth: 160 }}
            />
            <TextInput
              label="Экран"
              placeholder="440x956"
              value={profileScreen}
              onChange={(event) => setProfileScreen(event.currentTarget.value)}
              styles={inputLabelStyles}
              style={{ width: 120 }}
            />
            <TextInput
              label="iOS prefix"
              placeholder="26."
              value={profileIos}
              onChange={(event) => setProfileIos(event.currentTarget.value)}
              styles={inputLabelStyles}
              style={{ width: 100 }}
            />
            <Button size="sm" loading={updateProfile.isLoading} onClick={handleSaveProfile}>
              Сохранить
            </Button>
          </Group>
        </Paper>

        <div>
          <Text fw={500} mb="xs">
            Устройства по этой ссылке
          </Text>
          {!item.devices.length ? (
            <EmptyNote>По этой ссылке ещё никто не переходил</EmptyNote>
          ) : (
            <Table.ScrollContainer minWidth={900}>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Раз</Table.Th>
                    <Table.Th>IP</Table.Th>
                    <Table.Th>Модель</Table.Th>
                    <Table.Th>Тип</Table.Th>
                    <Table.Th>ОС</Table.Th>
                    <Table.Th>Android</Table.Th>
                    <Table.Th>iOS</Table.Th>
                    <Table.Th>Браузер</Table.Th>
                    <Table.Th>Первый</Table.Th>
                    <Table.Th>Последний</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {item.devices.map((device, index) => (
                    <Table.Tr key={`${device.ip}-${index}`}>
                      <Table.Td>
                        <Text ff="monospace" fw={600}>
                          {device.visitCount}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text ff="monospace" size="sm">
                          {device.ip}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <ModelCell {...device} />
                      </Table.Td>
                      <Table.Td>{device.deviceType || '—'}</Table.Td>
                      <Table.Td>{osLabel(device)}</Table.Td>
                      <Table.Td>
                        <Text ff="monospace" size="sm">
                          {device.androidVersion || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text ff="monospace" size="sm">
                          {device.iosVersion || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>{device.browser || '—'}</Table.Td>
                      <Table.Td>
                        <Text ff="monospace" size="xs">
                          {fmtDate(device.firstVisitAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text ff="monospace" size="xs">
                          {fmtDate(device.lastVisitAt)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </div>

        {item.visits.length > 0 && (
          <Accordion variant="contained">
            <Accordion.Item value="visits">
              <Accordion.Control>
                Все {item.visits.length} переходов по этой ссылке
              </Accordion.Control>
              <Accordion.Panel>
                <Table.ScrollContainer minWidth={800}>
                  <Table striped withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>#</Table.Th>
                        <Table.Th>Время</Table.Th>
                        <Table.Th>IP</Table.Th>
                        <Table.Th>Модель</Table.Th>
                        <Table.Th>ОС</Table.Th>
                        <Table.Th>Android</Table.Th>
                        <Table.Th>iOS</Table.Th>
                        <Table.Th>Браузер</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {item.visits.map((visit, index) => (
                        <Table.Tr key={visit._id}>
                          <Table.Td>{item.visits.length - index}</Table.Td>
                          <Table.Td>
                            <Text ff="monospace" size="xs">
                              {fmtDate(visit.createdAt)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text ff="monospace" size="sm">
                              {visit.ip}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <ModelCell {...visit} />
                          </Table.Td>
                          <Table.Td>{osLabel(visit)}</Table.Td>
                          <Table.Td>
                            <Text ff="monospace" size="sm">
                              {visit.androidVersion || '—'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text ff="monospace" size="sm">
                              {visit.iosVersion || '—'}
                            </Text>
                          </Table.Td>
                          <Table.Td>{visit.browser || '—'}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
      </Stack>
    </Paper>
  );
};
